import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { 
  Users, 
  Plus, 
  Search, 
  Filter,
  MapPin, 
  Phone, 
  Mail,
  Edit3,
  Eye,
  Calendar,
  Home,
  Zap,
  FileText,
  TrendingUp
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Clients = () => {
  const location = useLocation();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState(null);
  
  // Récupérer l'utilisateur connecté
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  
  const [newClient, setNewClient] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    adresse: '',
    ville: '',
    code_postal: '',
    commercial_id: currentUser.id || '',
    surface_toit: '',
    consommation_annuelle: '',
    notes: ''
  });

  useEffect(() => {
    loadClients();
    
    // Gérer la navigation depuis la recherche
    if (location.state?.selectedClientId) {
      setSelectedClientId(location.state.selectedClientId);
      // Scroll vers le client sélectionné après chargement
      setTimeout(() => {
        const element = document.getElementById(`client-${location.state.selectedClientId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.classList.add('highlight-client');
          setTimeout(() => element.classList.remove('highlight-client'), 3000);
        }
      }, 500);
    }
  }, [location.state]);

  const loadClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`);
      setClients(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/clients`, newClient);
      setClients([response.data, ...clients]);
      setShowAddForm(false);
      setNewClient({
        nom: '',
        prenom: '',
        email: '',
        telephone: '',
        adresse: '',
        ville: '',
        code_postal: '',
        commercial_id: currentUser.id || '',
        surface_toit: '',
        consommation_annuelle: '',
        notes: ''
      });
    } catch (error) {
      console.error('Erreur lors de la création du client:', error);
    }
  };

  const updateClientStatus = async (clientId, newStatus) => {
    try {
      await axios.put(`${API}/clients/${clientId}/status?status=${newStatus}`);
      setClients(clients.map(client => 
        client.id === clientId 
          ? { ...client, status: newStatus }
          : client
      ));
    } catch (error) {
      console.error('Erreur lors de la mise à jour du statut:', error);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      prospect: { label: 'Prospect', class: 'badge-info' },
      devis: { label: 'Devis', class: 'badge-warning' },
      client: { label: 'Client', class: 'badge-success' },
      installe: { label: 'Installé', class: 'badge-success' }
    };
    return badges[status] || { label: status, class: 'badge-secondary' };
  };

  const filteredClients = clients.filter(client => {
    const matchesSearch = `${client.prenom} ${client.nom} ${client.ville} ${client.telephone}`
      .toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || client.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusCount = (status) => {
    return clients.filter(client => client.status === status).length;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Gestion des Clients
          </h1>
          <p className="text-gray-600">
            Gérez vos prospects et clients panneaux solaires
          </p>
        </div>
        <div className="mt-4 lg:mt-0">
          <button 
            onClick={() => setShowAddForm(true)}
            className="btn btn-primary"
          >
            <Plus className="w-4 h-4" />
            Nouveau client
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="stats-card">
          <div className="flex items-center justify-between">
            <div>
              <div className="stats-value">{clients.length}</div>
              <div className="stats-label">Total</div>
            </div>
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
          </div>
        </div>
        <div className="stats-card">
          <div className="flex items-center justify-between">
            <div>
              <div className="stats-value">{getStatusCount('prospect')}</div>
              <div className="stats-label">Prospects</div>
            </div>
            <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-yellow-600" />
            </div>
          </div>
        </div>
        <div className="stats-card">
          <div className="flex items-center justify-between">
            <div>
              <div className="stats-value">{getStatusCount('client')}</div>
              <div className="stats-label">Clients actifs</div>
            </div>
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-green-600" />
            </div>
          </div>
        </div>
        <div className="stats-card">
          <div className="flex items-center justify-between">
            <div>
              <div className="stats-value">{getStatusCount('installe')}</div>
              <div className="stats-label">Installés</div>
            </div>
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Zap className="w-5 h-5 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher par nom, ville, téléphone..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="form-input pl-10"
                />
              </div>
            </div>
            <div className="lg:w-48">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="form-select"
              >
                <option value="all">Tous les statuts</option>
                <option value="prospect">Prospects</option>
                <option value="devis">Devis envoyé</option>
                <option value="client">Clients</option>
                <option value="installe">Installés</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Clients Table */}
      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th>Client</th>
              <th>Contact</th>
              <th>Adresse</th>
              <th>Collaborateur</th>
              <th>Infos techniques</th>
              <th>Statut</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredClients.map((client) => (
              <tr 
                key={client.id} 
                id={`client-${client.id}`}
                className={`transition-all duration-300 ${
                  selectedClientId === client.id ? 'bg-primary/10 ring-2 ring-primary/30' : ''
                }`}
              >
                <td>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-primary/20 to-primary/5 rounded-full flex items-center justify-center">
                      <Users className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">
                        {client.prenom} {client.nom}
                      </div>
                      <div className="text-sm text-gray-500">
                        Client depuis {new Date(client.created_at).toLocaleDateString('fr-FR')}
                      </div>
                    </div>
                  </div>
                </td>
                <td>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-sm">
                      <Phone className="w-3 h-3 text-gray-400" />
                      <span>{client.telephone}</span>
                    </div>
                    {client.email && (
                      <div className="flex items-center gap-2 text-sm">
                        <Mail className="w-3 h-3 text-gray-400" />
                        <span>{client.email}</span>
                      </div>
                    )}
                  </div>
                </td>
                <td>
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="w-3 h-3 text-gray-400" />
                    <div>
                      <div>{client.adresse}</div>
                      <div className="text-gray-500">{client.code_postal} {client.ville}</div>
                    </div>
                  </div>
                </td>
                <td>
                  {client.commercial ? (
                    <div className="text-sm">
                      <div className="font-medium text-gray-900">
                        {client.commercial.prenom} {client.commercial.nom}
                      </div>
                      <div className="text-xs text-gray-500">
                        {client.commercial.email}
                      </div>
                    </div>
                  ) : (
                    <span className="text-sm text-gray-400">Non assigné</span>
                  )}
                </td>
                <td>
                  <div className="space-y-1 text-sm">
                    {client.surface_toit && (
                      <div className="flex items-center gap-2">
                        <Home className="w-3 h-3 text-gray-400" />
                        <span>{client.surface_toit}m² toit</span>
                      </div>
                    )}
                    {client.consommation_annuelle && (
                      <div className="flex items-center gap-2">
                        <Zap className="w-3 h-3 text-gray-400" />
                        <span>{client.consommation_annuelle} kWh/an</span>
                      </div>
                    )}
                  </div>
                </td>
                <td>
                  <select
                    value={client.status}
                    onChange={(e) => updateClientStatus(client.id, e.target.value)}
                    className="form-select text-sm"
                  >
                    <option value="prospect">Prospect</option>
                    <option value="devis">Devis</option>
                    <option value="client">Client</option>
                    <option value="installe">Installé</option>
                  </select>
                </td>
                <td>
                  <div className="flex items-center gap-2">
                    <button className="btn btn-secondary btn-sm">
                      <Eye className="w-3 h-3" />
                    </button>
                    <button className="btn btn-secondary btn-sm">
                      <Edit3 className="w-3 h-3" />
                    </button>
                    <button className="btn btn-primary btn-sm">
                      <FileText className="w-3 h-3" />
                      Devis
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {filteredClients.length === 0 && (
          <div className="p-8 text-center text-gray-500">
            <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>Aucun client trouvé</p>
          </div>
        )}
      </div>

      {/* Add Client Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Nouveau Client</h2>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="form-group">
                  <label className="form-label">Prénom *</label>
                  <input
                    type="text"
                    value={newClient.prenom}
                    onChange={(e) => setNewClient({...newClient, prenom: e.target.value})}
                    className="form-input"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Nom *</label>
                  <input
                    type="text"
                    value={newClient.nom}
                    onChange={(e) => setNewClient({...newClient, nom: e.target.value})}
                    className="form-input"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    value={newClient.email}
                    onChange={(e) => setNewClient({...newClient, email: e.target.value})}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Téléphone *</label>
                  <input
                    type="tel"
                    value={newClient.telephone}
                    onChange={(e) => setNewClient({...newClient, telephone: e.target.value})}
                    className="form-input"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Adresse *</label>
                <input
                  type="text"
                  value={newClient.adresse}
                  onChange={(e) => setNewClient({...newClient, adresse: e.target.value})}
                  className="form-input"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="form-group">
                  <label className="form-label">Ville *</label>
                  <input
                    type="text"
                    value={newClient.ville}
                    onChange={(e) => setNewClient({...newClient, ville: e.target.value})}
                    className="form-input"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Code postal *</label>
                  <input
                    type="text"
                    value={newClient.code_postal}
                    onChange={(e) => setNewClient({...newClient, code_postal: e.target.value})}
                    className="form-input"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="form-group">
                  <label className="form-label">Surface toit (m²)</label>
                  <input
                    type="number"
                    value={newClient.surface_toit}
                    onChange={(e) => setNewClient({...newClient, surface_toit: e.target.value})}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Consommation annuelle (kWh)</label>
                  <input
                    type="number"
                    value={newClient.consommation_annuelle}
                    onChange={(e) => setNewClient({...newClient, consommation_annuelle: e.target.value})}
                    className="form-input"
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea
                  value={newClient.notes}
                  onChange={(e) => setNewClient({...newClient, notes: e.target.value})}
                  className="form-input"
                  rows="3"
                ></textarea>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="btn btn-secondary"
                >
                  Annuler
                </button>
                <button type="submit" className="btn btn-primary">
                  <Plus className="w-4 h-4" />
                  Créer le client
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Clients;